import { ArrowLeft, Pencil, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { CollapseSection, DetailKV, Section } from '@/components/ui/DetailSection';
import { getApiErrorMessage } from '@/utils/apiError';
import { AppearancePreview } from './AppearancePreview';
import { useColor, useToggleColor } from '../api';
import { hexToRgb } from '../utils/appearance';
import type { Library, ModelColor } from '../types';

export function ColorDetailPanel({
  id,
  canEdit,
  onEdit,
  onNavigate,
  onBack,
}: {
  id: string;
  canEdit: boolean;
  onEdit: (color: ModelColor) => void;
  onNavigate: (library: Library, id: string) => void;
  onBack: () => void;
}) {
  const { data: color, isLoading, isError } = useColor(id);
  const toggle = useToggleColor();

  if (isLoading) return <SpinnerOverlay />;
  if (isError || !color)
    return <EmptyState title="Barvu se nepodařilo načíst" description="Zkuste ji vybrat znovu." />;

  const setActive = async (active: boolean) => {
    try {
      await toggle.mutateAsync({ id: color.id, active });
      toast.success(active ? `${color.code} vrácena do palety` : `${color.code} odebrána z palety`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <article className="h-full overflow-y-auto" aria-label="Detail barvy">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-150 px-5 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Zpět na seznam"
            onClick={onBack}
          >
            <ArrowLeft />
          </Button>
          <h2 className="text-sm font-semibold">Detail barvy</h2>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={toggle.isLoading}
              onClick={() => setActive(!color.is_active)}
            >
              <Power />
              {color.is_active ? 'Odebrat z palety' : 'Vrátit do palety'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEdit(color)}>
              <Pencil />
              Upravit
            </Button>
          </div>
        )}
      </div>
      <div className="px-5 py-5">
        <AppearancePreview hex={color.hex} label={color.title} className="h-44 w-full" />
        <div className="mt-5 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-neutral-500">{color.code}</span>
            <h2 className="text-xl font-semibold text-neutral-900 break-words">{color.title}</h2>
            {!color.is_active && <Badge variant="warning">Odebráno z palety</Badge>}
          </div>
        </div>
        <Section title="Barva">
          <DetailKV
            label="HEX"
            value={
              <span className="inline-flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-sm border border-black/10"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="font-mono">{color.hex?.toUpperCase()}</span>
              </span>
            }
          />
          <DetailKV label="RGB" value={hexToRgb(color.hex)} />
          <DetailKV label="Kód barvy" value={color.color_code || '—'} />
          <DetailKV label="Kategorie" value={color.category_label} />
          <DetailKV
            label="Vlastnosti"
            value={
              color.properties.length ? (
                <span className="flex flex-wrap gap-1">
                  {color.properties.map((item) => (
                    <Badge key={item.id} variant="outline">
                      {item.title}
                    </Badge>
                  ))}
                </span>
              ) : (
                '—'
              )
            }
          />
          <DetailKV label="Číslo" value={color.code} />
        </Section>
        <CollapseSection
          title="Použito ve vzhledech"
          badge={color.appearance_count ?? color.appearances.length}
          defaultOpen
        >
          {color.appearances.length ? (
            <div className="flex flex-col items-start gap-1">
              {color.appearances.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="text-left text-sm text-primary-600 hover:underline py-1"
                  onClick={() => onNavigate('appearances', entry.id)}
                >
                  {entry.title}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-neutral-500">Zatím nepoužita v žádném aktivním vzhledu.</p>
          )}
        </CollapseSection>
      </div>
    </article>
  );
}
